export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type PwaAppKey = 'cs' | 'ds';

declare global {
  interface Window {
    __pwaInstallPrompts?: Partial<Record<PwaAppKey, BeforeInstallPromptEvent>>;
    __pwaInstallListenerAttached?: boolean;
  }
}

const PWA_INSTALL_AVAILABLE = 'pwa-install-available';
const PWA_MANIFEST_LINK_ID = 'dynamic-pwa-manifest';
const APPLE_TITLE_META = 'apple-mobile-web-app-title';
const PWA_DISMISS_PREFIX = 'pwa-install-dismissed-';
const PWA_MANIFEST_IDS: Record<PwaAppKey, string> = {
  cs: 'hlskin-cs',
  ds: 'hlskin-ds',
};

let activePwaAppKey: PwaAppKey | null = null;

export const getPwaAppKey = (pathname: string): PwaAppKey | null => {
  if (pathname === '/customer' || pathname.startsWith('/customer/')) {
    return 'cs';
  }

  if (pathname === '/distributor' || pathname.startsWith('/distributor/')) {
    return 'ds';
  }

  return null;
};

const getManifestHref = (pathname: string): string | null => {
  const key = getPwaAppKey(pathname);
  if (key === 'cs') return '/manifest-cs.json';
  if (key === 'ds') return '/manifest-ds.json';
  return null;
};

const getPwaAppLabel = (key: PwaAppKey): string =>
  key === 'cs' ? 'HL Skin CS' : 'HL Skin DS';

export const getDeferredPwaInstallPrompt = (
  pathname: string = window.location.pathname,
): BeforeInstallPromptEvent | null => {
  const key = getPwaAppKey(pathname);
  if (!key) return null;

  return window.__pwaInstallPrompts?.[key] ?? null;
};

export const clearDeferredPwaInstallPrompt = (
  pathname: string = window.location.pathname,
): void => {
  const key = getPwaAppKey(pathname);
  if (!key || !window.__pwaInstallPrompts) return;

  delete window.__pwaInstallPrompts[key];
  window.dispatchEvent(new Event(PWA_INSTALL_AVAILABLE));
};

export const dismissPwaInstallPrompt = (
  pathname: string = window.location.pathname,
): void => {
  const key = getPwaAppKey(pathname);
  if (!key) return;

  try {
    localStorage.setItem(`${PWA_DISMISS_PREFIX}${key}`, '1');
  } catch {
    // Ignore storage errors in restricted contexts
  }

  window.dispatchEvent(new Event(PWA_INSTALL_AVAILABLE));
};

export const isPwaInstallDismissed = (
  pathname: string = window.location.pathname,
): boolean => {
  const key = getPwaAppKey(pathname);
  if (!key) return false;

  try {
    return localStorage.getItem(`${PWA_DISMISS_PREFIX}${key}`) === '1';
  } catch {
    return false;
  }
};

export const initPwaInstallListener = (): void => {
  if (typeof window === 'undefined') return;

  if (!window.__pwaInstallPrompts) {
    window.__pwaInstallPrompts = {};
  }

  if (window.__pwaInstallListenerAttached) {
    return;
  }

  window.__pwaInstallListenerAttached = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    const key = getPwaAppKey(window.location.pathname);
    if (!key) return;

    window.__pwaInstallPrompts![key] = event as BeforeInstallPromptEvent;
    window.dispatchEvent(new Event(PWA_INSTALL_AVAILABLE));
  });

  window.addEventListener('appinstalled', () => {
    const key = getPwaAppKey(window.location.pathname);
    if (key && window.__pwaInstallPrompts) {
      delete window.__pwaInstallPrompts[key];
    }
    window.dispatchEvent(new Event(PWA_INSTALL_AVAILABLE));
  });
};

export const PWA_INSTALL_AVAILABLE_EVENT = PWA_INSTALL_AVAILABLE;

export const isStandalonePwa = (): boolean => {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

export const isPwaEntryPath = (pathname: string): boolean =>
  getPwaAppKey(pathname) !== null;

export const isPwaAppInstalled = async (
  pathname: string = window.location.pathname,
): Promise<boolean> => {
  const key = getPwaAppKey(pathname);
  if (!key || typeof navigator === 'undefined') return false;

  const getInstalledRelatedApps = (
    navigator as Navigator & {
      getInstalledRelatedApps?: () => Promise<Array<{ id?: string }>>;
    }
  ).getInstalledRelatedApps;

  if (!getInstalledRelatedApps) return false;

  try {
    const installedApps = await getInstalledRelatedApps.call(navigator);
    return installedApps.some((app) => app.id === PWA_MANIFEST_IDS[key]);
  } catch {
    return false;
  }
};

export const getPwaAppLabelForPath = (pathname: string): string => {
  const key = getPwaAppKey(pathname);
  return key ? getPwaAppLabel(key) : 'HL Skin';
};

export const updatePwaManifestForRoute = (pathname: string): void => {
  const key = getPwaAppKey(pathname);
  const manifestHref = getManifestHref(pathname);
  if (!key || !manifestHref) return;

  if (
    activePwaAppKey &&
    activePwaAppKey !== key &&
    !isStandalonePwa()
  ) {
    activePwaAppKey = key;
    window.location.replace(
      `${pathname}${window.location.search}${window.location.hash}`,
    );
    return;
  }

  activePwaAppKey = key;

  let meta = document.querySelector(`meta[name="${APPLE_TITLE_META}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', APPLE_TITLE_META);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', getPwaAppLabel(key));

  let link = document.getElementById(PWA_MANIFEST_LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = PWA_MANIFEST_LINK_ID;
    link.rel = 'manifest';
    document.head.appendChild(link);
  }

  if (link.getAttribute('href') !== manifestHref) {
    link.setAttribute('href', manifestHref);
  }
};
